/**
 * Frise de la campagne : un repère par niveau du mode limité, regroupés par royaume (DA).
 *
 * Elle répond d'un coup d'œil à « où en suis-je ? » : les niveaux atteints sont pleins, le suivant
 * est cerclé d'or, les autres restent en creux. Chaque repère porte le pictogramme de son élément
 * (triangle, disque, carré, losange) : la frise ne repose jamais sur la seule couleur.
 *
 * Les niveaux viennent du manifeste (aucun numéro ni royaume écrit en dur) : une campagne
 * republiée avec plus ou moins de niveaux s'affiche telle qu'elle est. La frise est décorative
 * pour les technologies d'assistance, qui lisent à la place la phrase de synthèse.
 */
import { useTranslation } from 'react-i18next';

import { IconeElement } from './IconeElement';
import { cleTraductionContenu, formaterNombre } from '../i18n';

import type { Element } from '@elemora/shared/content';

/** Niveau tel que la frise en a besoin (sous-ensemble d'une entrée du manifeste). */
export interface NiveauFrise {
  readonly id: string;
  readonly numero: number;
  readonly da: Element;
}

export interface ProprietesFriseCampagne {
  /** Niveaux du mode limité, dans n'importe quel ordre (la frise les trie par numéro). */
  readonly niveaux: readonly NiveauFrise[];
  /** Niveau maximum atteint, lu dans `/me` (0 pour un compte neuf). */
  readonly niveauMax: number;
}

interface Royaume {
  readonly da: Element;
  readonly niveaux: NiveauFrise[];
}

/** Regroupe les niveaux consécutifs d'une même DA : un royaume par série. */
export function regrouperParRoyaume(niveaux: readonly NiveauFrise[]): Royaume[] {
  const tries = [...niveaux].sort((a, b) => a.numero - b.numero);
  const royaumes: Royaume[] = [];
  for (const niveau of tries) {
    const dernier = royaumes.at(-1);
    if (dernier !== undefined && dernier.da === niveau.da) {
      dernier.niveaux.push(niveau);
    } else {
      royaumes.push({ da: niveau.da, niveaux: [niveau] });
    }
  }
  return royaumes;
}

export function FriseCampagne({ niveaux, niveauMax }: ProprietesFriseCampagne) {
  const { t, i18n } = useTranslation();
  const royaumes = regrouperParRoyaume(niveaux);
  const atteints = niveaux.filter((niveau) => niveau.numero <= niveauMax).length;

  return (
    <div className="frise" data-frise-campagne="oui">
      <p className="visuellement-masque">
        {t('accueil.campagne.resume', {
          count: atteints,
          atteints: formaterNombre(atteints, i18n.language),
          total: formaterNombre(niveaux.length, i18n.language),
        })}
      </p>
      <ol className="frise__royaumes" aria-hidden="true">
        {royaumes.map((royaume) => (
          <li
            className={`frise__royaume frise__royaume--${royaume.da}`}
            key={`${royaume.da}-${royaume.niveaux[0]?.numero ?? 0}`}
          >
            <span className="frise__nom">
              {t(cleTraductionContenu(`content.da.${royaume.da}.name`))}
            </span>
            <ol className="frise__niveaux">
              {royaume.niveaux.map((niveau) => {
                const etat =
                  niveau.numero <= niveauMax
                    ? 'atteint'
                    : niveau.numero === niveauMax + 1
                      ? 'suivant'
                      : 'a-venir';
                return (
                  <li
                    className={`frise__niveau frise__niveau--${etat}`}
                    key={niveau.id}
                    data-niveau={niveau.numero}
                  >
                    <IconeElement element={niveau.da} />
                  </li>
                );
              })}
            </ol>
          </li>
        ))}
      </ol>
    </div>
  );
}
